import { CheckIcon } from '@heroicons/react/outline';
import appI18nInstance from 'i18n';
import {
  SubscriptionCounterKeysEnum,
  type SubscriptionPlan,
} from 'types/subscriptions.types';
import { SubscriptionPlanNameEnum } from 'types/users.types';
import formatMoney from 'utils/formatMoney';

const paywall = {
  plans: {
    [SubscriptionPlanNameEnum.small1]: {
      netPrice: 17.5,
    },
    [SubscriptionPlanNameEnum.small12]: {
      netPrice: 15,
    },
  },
  getPlanFeatures: (
    features: SubscriptionPlan['features'],
  ): Record<
    string,
    {
      features: ({ content: string } | null)[];
    }
  > => ({
    [SubscriptionPlanNameEnum.basic1]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.mobile_access',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.unlimited_docs',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.one_bank_account',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.accountant_access',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.ai_tax_advisor',
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.basic12]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.mobile_access',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.unlimited_docs',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.one_bank_account',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.accountant_access',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.ai_tax_advisor',
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.free]: {
      features: [
        {
          content: appI18nInstance.t('plan_features.mobile_only'),
        },
        {
          content: appI18nInstance.t('plan_feature.bank_connected', {
            numberOfAccounts: 1,
          }),
        },
        {
          content: appI18nInstance.t('plan_feature.free_peppol'),
        },
        {
          content: appI18nInstance.t('plan_feature.mobile_unlimited_invoices'),
        },
        {
          content: appI18nInstance.t('plan_feature.mobile_monthly_expenses', {
            limit: 5,
          }),
        },
        {
          content: appI18nInstance.t('plan_features.tax_advisor', {
            numberofQuestions: 5,
          }),
        },
      ],
    },
    [SubscriptionPlanNameEnum.pro1]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.automatic_tax_return',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.tax_guarantee_amount',
            { amount: formatMoney(500) },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.mobile_desktop_access',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.bank_accounts',
            {
              numOfAccounts: 5,
            },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.ai_tax_advisor',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.e_invoicing',
          ),
        },
        {
          content: appI18nInstance.t('subscriptions_screen.features.expenses'),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.cancel_anytime',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.deductible',
            { percent: 120 },
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.pro12]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.automatic_tax_return',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.tax_guarantee_amount',
            { amount: formatMoney(500) },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.mobile_desktop_access',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.bank_accounts',
            {
              numOfAccounts: 5,
            },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.ai_tax_advisor',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.e_invoicing',
          ),
        },
        {
          content: appI18nInstance.t('subscriptions_screen.features.expenses'),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.cancel_anytime',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.deductible',
            { percent: 120 },
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.small1]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.automatic_tax_return',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.tax_guarantee_amount',
            { amount: formatMoney(500) },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.mobile_desktop_access',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.bank_accounts',
            {
              numOfAccounts: 5,
            },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.ai_tax_advisor',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.e_invoicing',
          ),
        },
        {
          content: appI18nInstance.t('subscriptions_screen.features.expenses'),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.cancel_anytime',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.deductible',
            { percent: 120 },
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.small12]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.automatic_tax_return',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.tax_guarantee_amount',
            { amount: formatMoney(500) },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.mobile_desktop_access',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.bank_accounts',
            {
              numOfAccounts: 5,
            },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.ai_tax_advisor',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.e_invoicing',
          ),
        },
        {
          content: appI18nInstance.t('subscriptions_screen.features.expenses'),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.cancel_anytime',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.deductible',
            { percent: 120 },
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.small_max1]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.tax_guarantee_amount',
            { amount: formatMoney(10000) },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.direct_tax_coach',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.unlimited_ai_tax_advisor',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.cancel_anytime',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.bank_accounts',
            {
              numOfAccounts: 10,
            },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.deductible',
            { percent: 120 },
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.small_max12]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.tax_guarantee_amount',
            { amount: formatMoney(10000) },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.direct_tax_coach',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.unlimited_ai_tax_advisor',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.cancel_anytime',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.bank_accounts',
            {
              numOfAccounts: 10,
            },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.deductible',
            { percent: 120 },
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.pro_max1]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.tax_guarantee_amount',
            { amount: formatMoney(10000) },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.direct_tax_coach',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.unlimited_ai_tax_advisor',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.cancel_anytime',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.bank_accounts',
            {
              numOfAccounts: 10,
            },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.deductible',
            { percent: 120 },
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.pro_max12]: {
      features: [
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.tax_guarantee_amount',
            { amount: formatMoney(10000) },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.direct_tax_coach',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.unlimited_ai_tax_advisor',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.cancel_anytime',
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.bank_accounts',
            {
              numOfAccounts: 10,
            },
          ),
        },
        {
          content: appI18nInstance.t(
            'subscriptions_screen.features.deductible',
            { percent: 120 },
          ),
        },
      ],
    },
    [SubscriptionPlanNameEnum.e_invoicing1]: {
      features: [
        {
          content: appI18nInstance.t('plan_features.web_access'),
        },
        {
          content: appI18nInstance.t('plan_feature.unlimited_einvoices'),
        },
        {
          content: appI18nInstance.t('plan_feature.unlimited_quotes'),
        },
        {
          content: appI18nInstance.t('plan_feature.installment_invoices'),
        },
        {
          content: appI18nInstance.t('plan_feature.recurring_invoices'),
        },
      ],
    },
    [SubscriptionPlanNameEnum.e_invoicing12]: {
      features: [
        {
          content: appI18nInstance.t('plan_features.web_access'),
        },
        {
          content: appI18nInstance.t('plan_feature.unlimited_einvoices'),
        },
        {
          content: appI18nInstance.t('plan_feature.unlimited_quotes'),
        },
        {
          content: appI18nInstance.t('plan_feature.installment_invoices'),
        },
        {
          content: appI18nInstance.t('plan_feature.recurring_invoices'),
        },
      ],
    },
    [SubscriptionPlanNameEnum.bookkeeping1]: {
      features: [
        {
          content:
            features?.counters?.bankAccounts?.total === 'INFINITE'
              ? appI18nInstance.t('plan_features.unlimited_banks')
              : appI18nInstance.t('plan_features.bank_connections', {
                  numberOfAccounts: features?.counters?.bankAccounts
                    ?.total as number,
                }),
        },
        {
          content: appI18nInstance.t('plan_feature.accounting_invoices'),
        },
        {
          content: appI18nInstance.t('plan_feature.unlimited_expenses'),
        },
        {
          content: appI18nInstance.t('plan_feature.support_advice'),
        },
        {
          content: appI18nInstance.t('plan_feature.net_income'),
        },
        {
          content: appI18nInstance.t('plan_feature.free_income_tax_franchisee'),
        },
      ],
    },
    [SubscriptionPlanNameEnum.bookkeeping12]: {
      features: [
        {
          content:
            features?.counters?.bankAccounts?.total === 'INFINITE'
              ? appI18nInstance.t('plan_features.unlimited_banks')
              : appI18nInstance.t('plan_features.bank_connections', {
                  numberOfAccounts: features?.counters?.bankAccounts
                    ?.total as number,
                }),
        },
        {
          content: appI18nInstance.t('plan_feature.accounting_invoices'),
        },
        {
          content: appI18nInstance.t('plan_feature.unlimited_expenses'),
        },
        {
          content: appI18nInstance.t('plan_feature.support_advice'),
        },
        {
          content: appI18nInstance.t('plan_feature.net_income'),
        },
        {
          content: appI18nInstance.t('plan_feature.free_income_tax_franchisee'),
        },
      ],
    },
    [SubscriptionPlanNameEnum.taxes1]: {
      features: [
        {
          content: appI18nInstance.t('plan_feature.vat_return'),
        },
        {
          content: appI18nInstance.t('plan_feature.intracom_client_isting'),
        },
        {
          content: appI18nInstance.t('plan_feature.income_tax'),
        },
        {
          content: appI18nInstance.t('plan_features.tax_guarantee_500'),
        },
        {
          content: appI18nInstance.t('plan_feature.support_advice'),
        },
        {
          content:
            features?.counters?.taxAdvisorMonthlyMessages?.total === 'INFINITE'
              ? appI18nInstance.t('plan_features.tax_advisor_unlimited')
              : appI18nInstance.t('plan_features.tax_advisor', {
                  numberofQuestions:
                    features?.counters?.taxAdvisorMonthlyMessages?.total,
                }),
        },
        {
          content: appI18nInstance.t('plan_features.tax_coaches_webinars'),
        },
      ],
    },
    [SubscriptionPlanNameEnum.taxes12]: {
      features: [
        {
          content: appI18nInstance.t('plan_feature.vat_return'),
        },
        {
          content: appI18nInstance.t('plan_feature.intracom_client_isting'),
        },
        {
          content: appI18nInstance.t('plan_feature.income_tax'),
        },
        {
          content: appI18nInstance.t('plan_features.tax_guarantee_500'),
        },
        {
          content: appI18nInstance.t('plan_feature.support_advice'),
        },
        {
          content:
            features?.counters?.taxAdvisorMonthlyMessages?.total === 'INFINITE'
              ? appI18nInstance.t('plan_features.tax_advisor_unlimited')
              : appI18nInstance.t('plan_features.tax_advisor', {
                  numberofQuestions:
                    features?.counters?.taxAdvisorMonthlyMessages?.total,
                }),
        },
        {
          content: appI18nInstance.t('plan_features.tax_coaches_webinars'),
        },
      ],
    },
    [SubscriptionPlanNameEnum.max1]: {
      features: [
        {
          content:
            features?.counters?.bankAccounts?.total === 'INFINITE'
              ? appI18nInstance.t('plan_features.unlimited_banks')
              : appI18nInstance.t('plan_features.bank_connections', {
                  numberOfAccounts: features?.counters?.bankAccounts
                    ?.total as number,
                }),
        },
        {
          content: appI18nInstance.t('plan_features.tax_guarantee_10k'),
        },
        {
          content: appI18nInstance.t('plan_feature.tax_coaches_onboarding'),
        },
        {
          content: appI18nInstance.t('plan_features.personal_tax_coach'),
        },
        {
          content: appI18nInstance.t('plan_features.tax_advisor_unlimited'),
        },
      ],
    },
    [SubscriptionPlanNameEnum.max12]: {
      features: [
        {
          content:
            features?.counters?.bankAccounts?.total === 'INFINITE'
              ? appI18nInstance.t('plan_features.unlimited_banks')
              : appI18nInstance.t('plan_features.bank_connections', {
                  numberOfAccounts: features?.counters?.bankAccounts
                    ?.total as number,
                }),
        },
        {
          content: appI18nInstance.t('plan_features.tax_guarantee_10k'),
        },
        {
          content: appI18nInstance.t('plan_feature.tax_coaches_onboarding'),
        },
        {
          content: appI18nInstance.t('plan_features.personal_tax_coach'),
        },
        {
          content: appI18nInstance.t('plan_features.tax_advisor_unlimited'),
        },
      ],
    },
  }),
  paywallFeaturesList: {
    [SubscriptionPlanNameEnum.e_invoicing1]: () => [
      {
        title: appI18nInstance.t('plan_features.create_invoices_web.title'),
        description: appI18nInstance.t(
          'plan_features.create_invoices_web.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.customize_invoices'),
        description: appI18nInstance.t(
          'plan_features.customize_invoices.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.installment_invoices'),
        description: appI18nInstance.t(
          'plan_features.installment_invoices.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.recurring_invoices'),
        description: appI18nInstance.t(
          'plan_features.recurring_invoices.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.quotes'),
        description: appI18nInstance.t('plan_features.quotes.description'),
        Icon: CheckIcon,
      },
    ],
    [SubscriptionPlanNameEnum.e_invoicing12]: () => [
      {
        title: appI18nInstance.t('plan_features.create_invoices_web.title'),
        description: appI18nInstance.t(
          'plan_features.create_invoices_web.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.customize_invoices'),
        description: appI18nInstance.t(
          'plan_features.customize_invoices.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.installment_invoices'),
        description: appI18nInstance.t(
          'plan_features.installment_invoices.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.recurring_invoices'),
        description: appI18nInstance.t(
          'plan_features.recurring_invoices.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.quotes'),
        description: appI18nInstance.t('plan_features.quotes.description'),
        Icon: CheckIcon,
      },
    ],
    [SubscriptionPlanNameEnum.bookkeeping1]: (
      features: SubscriptionPlan['features'],
    ) => [
      {
        title: appI18nInstance.t('plan_features.complete_web'),
        description: appI18nInstance.t(
          'plan_features.complete_web.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.unlimited_documents'),
        description: appI18nInstance.t(
          'plan_features.unlimited_documents.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.cash_available'),
        description: appI18nInstance.t(
          'plan_features.cash_available.description',
        ),
        Icon: CheckIcon,
      },
      {
        title:
          features?.counters?.bankAccounts?.total === 'INFINITE'
            ? appI18nInstance.t('plan_features.unlimited_banks')
            : appI18nInstance.t('plan_features.bank_connections', {
                numberOfAccounts: features?.counters?.bankAccounts
                  ?.total as number,
              }),
        description: appI18nInstance.t(
          'plan_features.bank_connections.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.amortization'),
        description: appI18nInstance.t(
          'plan_features.amortization.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.export_docs'),
        description: appI18nInstance.t('plan_features.export_docs.description'),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.tax_tips'),
        description: appI18nInstance.t('plan_features.tax_tips.description'),
        Icon: CheckIcon,
      },
    ],
    [SubscriptionPlanNameEnum.bookkeeping12]: (
      features: SubscriptionPlan['features'],
    ) => [
      {
        title: appI18nInstance.t('plan_features.complete_web'),
        description: appI18nInstance.t(
          'plan_features.complete_web.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.unlimited_documents'),
        description: appI18nInstance.t(
          'plan_features.unlimited_documents.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.cash_available'),
        description: appI18nInstance.t(
          'plan_features.cash_available.description',
        ),
        Icon: CheckIcon,
      },
      {
        title:
          features?.counters?.bankAccounts?.total === 'INFINITE'
            ? appI18nInstance.t('plan_features.unlimited_banks')
            : appI18nInstance.t('plan_features.bank_connections', {
                numberOfAccounts: features?.counters?.bankAccounts
                  ?.total as number,
              }),
        description: appI18nInstance.t(
          'plan_features.bank_connections.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.amortization'),
        description: appI18nInstance.t(
          'plan_features.amortization.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.export_docs'),
        description: appI18nInstance.t('plan_features.export_docs.description'),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.tax_tips'),
        description: appI18nInstance.t('plan_features.tax_tips.description'),
        Icon: CheckIcon,
      },
    ],
    [SubscriptionPlanNameEnum.taxes1]: (
      features: SubscriptionPlan['features'],
    ) => [
      {
        title: appI18nInstance.t('plan_features.complete_web'),
        description: appI18nInstance.t(
          'plan_features.complete_web.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.all_taxes'),
        description: appI18nInstance.t('plan_features.all_taxes.description'),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.one_one_tax_coaches'),
        description: appI18nInstance.t(
          'plan_features.one_one_tax_coaches.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.view_tax_payments'),
        description: appI18nInstance.t(
          'plan_features.view_tax_payments.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.unlimited_documents'),
        description: appI18nInstance.t(
          'plan_features.unlimited_documents.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_feature.tax_advisor_access'),
        description:
          features?.counters?.taxAdvisorMonthlyMessages?.total === 'INFINITE'
            ? appI18nInstance.t('plan_features.tax_advisor_unlimited')
            : appI18nInstance.t('plan_features.tax_advisor', {
                numberofQuestions:
                  features?.counters?.taxAdvisorMonthlyMessages?.total,
              }),
        Icon: CheckIcon,
      },
    ],
    [SubscriptionPlanNameEnum.taxes12]: (
      features: SubscriptionPlan['features'],
    ) => [
      {
        title: appI18nInstance.t('plan_features.complete_web'),
        description: appI18nInstance.t(
          'plan_features.complete_web.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.all_taxes'),
        description: appI18nInstance.t('plan_features.all_taxes.description'),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.one_one_tax_coaches'),
        description: appI18nInstance.t(
          'plan_features.one_one_tax_coaches.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.view_tax_payments'),
        description: appI18nInstance.t(
          'plan_features.view_tax_payments.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.unlimited_documents'),
        description: appI18nInstance.t(
          'plan_features.unlimited_documents.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_feature.tax_advisor_access'),
        description:
          features?.counters?.taxAdvisorMonthlyMessages?.total === 'INFINITE'
            ? appI18nInstance.t('plan_features.tax_advisor_unlimited')
            : appI18nInstance.t('plan_features.tax_advisor', {
                numberofQuestions:
                  features?.counters?.taxAdvisorMonthlyMessages?.total,
              }),
        Icon: CheckIcon,
      },
    ],
    [SubscriptionPlanNameEnum.max1]: () => [
      {
        title: appI18nInstance.t('plan_features.banking_spaces'),
        description: appI18nInstance.t(
          'plan_features.banking_spaces.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.personal_tax_coach'),
        description: appI18nInstance.t(
          'plan_features.personal_tax_coach.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.free_physical_card'),
        description: appI18nInstance.t(
          'plan_features.free_physical_card.description',
        ),
        Icon: CheckIcon,
      },
    ],
    [SubscriptionPlanNameEnum.max12]: () => [
      {
        title: appI18nInstance.t('plan_features.banking_spaces'),
        description: appI18nInstance.t(
          'plan_features.banking_spaces.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.personal_tax_coach'),
        description: appI18nInstance.t(
          'plan_features.personal_tax_coach.description',
        ),
        Icon: CheckIcon,
      },
      {
        title: appI18nInstance.t('plan_features.free_physical_card'),
        description: appI18nInstance.t(
          'plan_features.free_physical_card.description',
        ),
        Icon: CheckIcon,
      },
    ],
  },
};

export default paywall;
